import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Camera, Folder, Image, Video, FileText, Download, Share2, Trash2, Grid3x3, List, Eye, Upload, Star, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import UserMenu from '../components/UserMenu';
import { bookings as bookingsApi, assets as assetsApi, shares as sharesApi, reviews as reviewsApi } from '../lib/api';
import { formatDate, getStoredUser } from '../lib/auth';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

const AssetVault = () => {
  const { bookingId } = useParams();
  const user = getStoredUser();

  const [bookingList, setBookingList] = React.useState([]);
  const [booking, setBooking] = React.useState(null);
  const [assetList, setAssetList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [viewMode, setViewMode] = React.useState('grid');
  const [selectedAssets, setSelectedAssets] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
  const [shareForm, setShareForm] = React.useState({ password: '', watermark_enabled: true, download_limit: '', expires_in_hours: '168' });
  const [shareResult, setShareResult] = React.useState(null);
  const [creatingShare, setCreatingShare] = React.useState(false);
  const [reviewForm, setReviewForm] = React.useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const isClientOwner = booking && user && booking.client_id === user.id;
  const isUploader = booking && user && booking.photographer?.user_id === user.id;

  const loadBookingList = React.useCallback(async () => {
    try {
      const { data } = await bookingsApi.list();
      setBookingList(data.data || []);
    } catch {
      toast.error('Could not load your bookings.');
    }
  }, []);

  const loadVault = React.useCallback(async () => {
    if (!bookingId) {
      setLoading(true);
      await loadBookingList();
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [{ data: bookingData }, { data: assetsData }] = await Promise.all([
        bookingsApi.get(bookingId),
        assetsApi.list(bookingId),
      ]);
      setBooking(bookingData);
      setAssetList(assetsData.data || []);
    } catch (err) {
      toast.error('Could not load this vault.');
    } finally {
      setLoading(false);
    }
  }, [bookingId, loadBookingList]);

  React.useEffect(() => {
    loadVault();
    setSelectedAssets([]);
  }, [loadVault]);

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets((prev) => (prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]));
  };

  const handlePreview = async (assetId) => {
    try {
      const { data } = await assetsApi.download(assetId);
      window.open(data.url, '_blank', 'noopener');
    } catch {
      toast.error('Could not generate a link for this file.');
    }
  };

  const handleDownload = async (assetId) => {
    try {
      const { data } = await assetsApi.download(assetId);
      const a = document.createElement('a');
      a.href = data.url;
      a.click();
    } catch {
      toast.error('Could not download this file.');
    }
  };

  const handleDelete = async (assetId) => {
    try {
      await assetsApi.remove(assetId);
      toast.success('File deleted.');
      loadVault();
    } catch {
      toast.error('Could not delete this file.');
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFilesSelected = async (fileList) => {
    if (!fileList.length) return;
    const formData = new FormData();
    Array.from(fileList).forEach((file) => formData.append('files[]', file));
    setUploading(true);
    try {
      await assetsApi.upload(bookingId, formData);
      toast.success(`${fileList.length} file(s) uploaded.`);
      loadVault();
    } catch {
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const openShareDialog = () => {
    if (selectedAssets.length === 0) {
      toast.error('Select at least one file to share.');
      return;
    }
    setShareResult(null);
    setShareDialogOpen(true);
  };

  const handleCreateShare = async () => {
    setCreatingShare(true);
    try {
      const { data } = await sharesApi.create(bookingId, {
        asset_ids: selectedAssets,
        password: shareForm.password || undefined,
        watermark_enabled: shareForm.watermark_enabled,
        download_limit: shareForm.download_limit ? Number(shareForm.download_limit) : undefined,
        expires_in_hours: shareForm.expires_in_hours ? Number(shareForm.expires_in_hours) : undefined,
      });
      setShareResult(data.share_url);
      toast.success('Share link created!');
    } catch {
      toast.error('Could not create a share link.');
    } finally {
      setCreatingShare(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      await reviewsApi.create(bookingId, reviewForm);
      toast.success('Thanks for your review!');
      loadVault();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not submit your review.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // No booking selected yet - show a picker.
  if (!bookingId) {
    return (
      <div className="dark min-h-screen bg-background text-foreground">
        <header className="border-b border-border bg-card sticky top-0 z-50">
          <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest Vault</span>
            </Link>
            <div className="flex items-center gap-2">
              <UserMenu user={user} />
            </div>
          </div>
        </header>
        <div className="px-6 md:px-12 lg:px-24 py-12">
          <h1 className="font-playfair text-3xl font-bold mb-6">Choose a Booking</h1>
          {loading && <p className="text-muted-foreground">Loading…</p>}
          {!loading && bookingList.length === 0 && <p className="text-muted-foreground">You have no bookings with a vault yet.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bookingList.map((b) => (
              <Link key={b.id} to={`/vault/${b.id}`}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`vault-booking-${b.id}`}>
                  <CardContent className="p-6 flex items-center gap-4">
                    <Folder className="w-8 h-8 text-primary" />
                    <div>
                      <div className="font-semibold">{b.service?.title || b.event_type || 'Booking'}</div>
                      <div className="text-sm text-muted-foreground">
                        {b.photographer?.business_name || b.client?.name} • {formatDate(b.event_date)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const totalBytes = assetList.reduce((sum, a) => sum + Number(a.size || 0), 0);

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/vault" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest Vault</span>
            </Link>
            <div className="flex items-center space-x-4">
              {selectedAssets.length > 0 && (
                <Badge variant="secondary" data-testid="selection-count">{selectedAssets.length} selected</Badge>
              )}
              <Link to="/vault"><Button variant="ghost" size="sm" data-testid="dashboard-link">All Vaults</Button></Link>
              <UserMenu user={user} />
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-muted-foreground mb-6" data-testid="breadcrumb">
          <Link to="/vault" className="hover:text-foreground">My Vaults</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground" data-testid="current-folder">{booking?.service?.title || booking?.event_type || 'Booking'}</span>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="text-sm text-muted-foreground">
            {isUploader ? `Client: ${booking?.client?.name}` : `Photographer: ${booking?.photographer?.business_name}`}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {isUploader && (
              <>
                <input type="file" multiple ref={fileInputRef} className="hidden" onChange={(e) => handleFilesSelected(e.target.files)} />
                <Button size="sm" onClick={handleUploadClick} disabled={uploading} data-testid="upload-button">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </>
            )}
            {selectedAssets.length > 0 && (
              <>
                {isClientOwner && (
                  <Button variant="outline" size="sm" onClick={openShareDialog} data-testid="share-selected">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                )}
                {isClientOwner && (
                  <Button variant="outline" size="sm" onClick={() => selectedAssets.forEach(handleDelete)} data-testid="delete-selected">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </>
            )}
            <div className="border-l border-border pl-2 ml-2 flex gap-1">
              <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="icon" onClick={() => setViewMode('grid')} data-testid="view-grid">
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="icon" onClick={() => setViewMode('list')} data-testid="view-list">
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6 mb-8 text-sm text-muted-foreground" data-testid="vault-stats">
          <div>{assetList.length} items</div>
          <div>•</div>
          <div>{formatBytes(totalBytes)} total</div>
        </div>

        {loading && <p className="text-muted-foreground">Loading…</p>}
        {!loading && assetList.length === 0 && (
          <div className="text-center py-24 text-muted-foreground" data-testid="empty-vault">
            {isUploader ? 'No files uploaded yet - use Upload to deliver assets to your client.' : 'Your photographer hasn\'t uploaded any files yet.'}
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && assetList.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1" data-testid="asset-grid">
            {assetList.map((asset) => {
              const isVideo = asset.mime_type?.startsWith('video');
              const isImage = asset.mime_type?.startsWith('image');
              return (
                <div
                  key={asset.id}
                  className={`relative group aspect-square bg-muted/30 cursor-pointer overflow-hidden flex items-center justify-center ${selectedAssets.includes(asset.id) ? 'ring-4 ring-primary' : ''}`}
                  onClick={() => toggleAssetSelection(asset.id)}
                  data-testid={`asset-${asset.id}`}
                >
                  {isImage ? <Image className="w-10 h-10 text-muted-foreground" /> : isVideo ? <Video className="w-10 h-10 text-muted-foreground" /> : <FileText className="w-10 h-10 text-muted-foreground" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <div className="text-sm font-medium truncate" data-testid={`asset-name-${asset.id}`}>{asset.file_name}</div>
                      <div className="text-xs opacity-75" data-testid={`asset-size-${asset.id}`}>{formatBytes(asset.size)}</div>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex gap-1">
                      <Button size="icon" variant="secondary" className="h-8 w-8 bg-black/70 hover:bg-black/90" onClick={(e) => { e.stopPropagation(); handlePreview(asset.id); }} data-testid={`preview-${asset.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="secondary" className="h-8 w-8 bg-black/70 hover:bg-black/90" onClick={(e) => { e.stopPropagation(); handleDownload(asset.id); }} data-testid={`download-${asset.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && assetList.length > 0 && (
          <div className="space-y-1" data-testid="asset-list">
            {assetList.map((asset) => (
              <div
                key={asset.id}
                className={`flex items-center gap-4 p-4 rounded-sm hover:bg-muted/30 cursor-pointer transition-colors ${selectedAssets.includes(asset.id) ? 'bg-primary/10' : ''}`}
                onClick={() => toggleAssetSelection(asset.id)}
                data-testid={`asset-list-${asset.id}`}
              >
                {asset.mime_type?.startsWith('video') ? <Video className="w-8 h-8 text-muted-foreground" /> : asset.mime_type?.startsWith('image') ? <Image className="w-8 h-8 text-muted-foreground" /> : <FileText className="w-8 h-8 text-muted-foreground" />}
                <div className="flex-1">
                  <div className="font-medium" data-testid={`list-name-${asset.id}`}>{asset.file_name}</div>
                  <div className="text-sm text-muted-foreground">{formatBytes(asset.size)}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handlePreview(asset.id); }} data-testid={`list-preview-${asset.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDownload(asset.id); }} data-testid={`list-download-${asset.id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review prompt for completed bookings */}
        {isClientOwner && booking?.status === 'completed' && !booking?.review && (
          <Card className="mt-12" data-testid="review-card">
            <CardContent className="p-6">
              <h3 className="font-playfair text-xl font-semibold mb-4 flex items-center"><Star className="w-5 h-5 mr-2" />Rate this photographer</h3>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button type="button" key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`rating-star-${n}`}>
                      <Star className={`w-6 h-6 ${n <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`} />
                    </button>
                  ))}
                </div>
                <Textarea placeholder="Share your experience..." value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} data-testid="review-comment" />
                <Button type="submit" disabled={submittingReview} data-testid="submit-review">
                  {submittingReview ? 'Submitting…' : 'Submit Review'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>Share {selectedAssets.length} file(s)</DialogTitle>
          </DialogHeader>
          {!shareResult ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Password (optional)</label>
                <Input type="text" placeholder="Leave blank for no password" value={shareForm.password} onChange={(e) => setShareForm({ ...shareForm, password: e.target.value })} data-testid="share-password" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Download limit</label>
                  <Input type="number" placeholder="Unlimited" value={shareForm.download_limit} onChange={(e) => setShareForm({ ...shareForm, download_limit: e.target.value })} data-testid="share-download-limit" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Expires in (hours)</label>
                  <Input type="number" value={shareForm.expires_in_hours} onChange={(e) => setShareForm({ ...shareForm, expires_in_hours: e.target.value })} data-testid="share-expiry" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={shareForm.watermark_enabled} onChange={(e) => setShareForm({ ...shareForm, watermark_enabled: e.target.checked })} />
                Watermark previews
              </label>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Anyone with this link can view the shared files{shareForm.password ? ' (password required)' : ''}.</p>
              <div className="flex gap-2">
                <Input readOnly value={shareResult} data-testid="share-url" />
                <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(shareResult); toast.success('Copied!'); }}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            {!shareResult && (
              <Button onClick={handleCreateShare} disabled={creatingShare} data-testid="create-share-button">
                {creatingShare ? 'Creating…' : 'Create Share Link'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetVault;
